import { Fragment, useRef, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import CloseModal from "../form/CloseModal";
import EditComposerModal from './EditComposerModal';
import { useAuth } from 'hooks/auth';

const Block = ({ title, value, classCustom = "whitespace-nowrap text-sm font-semibold text-gray-500" }) => {
  return (
    <div className="flex flex-row justify-between border-b">
      <span className={classCustom}>
        {title}
      </span>
      <span className={"text-md text-primary"}>
        {value}
      </span>
    </div>
  )
}

const showNameByLink = (link) => {

  let newLink;

  const newValue = link?.split("/");

  if (newValue?.length > 1) {

    const withWWW = newValue[2].split(".");

    if (withWWW.length === 2) {

      newLink = newValue[2];

    } else {

      newLink = withWWW[1] + '.' + withWWW[2];
    }

  } else if (newValue?.length == 1) {

    const withWWW = newValue[0].split(".");

    if (withWWW.length === 2) {

      newLink = withWWW;

    } else {

      newLink = withWWW[1] + '.' + withWWW[2];
    }

  }

  return newLink;

}

const BlockLink = ({ content}) => {

  return(
    <div>
      <ul className="list-none ml-4 text-sm text-gray-900">
        {Array.isArray(content) && content.map((item, index)=> (
          <li key={index}> <a className="text-indigo-600 hover:text-pblue cursor-pointer" href={item} target="_blank">{showNameByLink(item)}</a></li>
        ))}
      </ul>
    </div>
  )
}

const classCustom = "whitespace-nowrap overflow-x-auto h-6 text-sm font-semibold text-gray-500";

export default function ShowComposer({ open, setOpen, composerToShow }) {

  const { user } = useAuth();

  const cancelButtonRef = useRef(null);

  const [openEditComposerModal, setOpenEditComposerModal] = useState(false),
    [openLoginModal, setOpenLoginModal] = useState(false),
    [data, setData] = useState({});

  useEffect(() => {
    if (!open) {
      setData({});
    } else {
      setData(composerToShow);
    }
  }, [open]);

  const handleUpdateComposer = () => {

    if(!user){
      window.location.pathname = `/auth/login/url=""`;
    }else {
      setOpenEditComposerModal(true);
    }

  }

  return (
    <>
     <EditComposerModal 
        open={openEditComposerModal} 
        setOpen={setOpenEditComposerModal} 
        composerToEdit={data}
        onClose={()=>setOpenEditComposerModal(false)}
      >
      </EditComposerModal>
  
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <div
          className="flex items-start md:items-end justify-center min-h-screen pt-4 px-4 pt-24 md:pb-20 text-center sm:block
         sm:p-0"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom rounded-xl 
              text-left 
              transform transition-all 
              bg-white
              sm:my-8 sm:align-middle sm:max-w-xl sm:w-full"
            >
              <div className="flex flex-col items-center justify-center">

                <div className="w-full flex flex-col justify-center bg-pblue p-4 rounded-t-xl">
                  <span className="mb-2 text-lg md:text-xl text-center font-bold text-white">ABOUT THIS COMPOSER</span>
                </div>

                <div className="px-24 w-full flex flex-col space-y-5 my-8">
                  <Block value={data?.firstName} title="FirstName" />
                  <Block value={data?.lastName} title="LastName" />
                  <Block value={data?.gender} title="Gender" />
                  <Block value={data?.dateOfBirth} title="Date of Birth" />
                  {data?.dateOfDeath && <Block value={data?.dateOfDeath} title="Date of death" />}
                  <Block value={data?.nationality_id} title="Nationality" />
                  {data?.webSiteLink&&<div className="flex flex-row justify-between border-b">
                    <span className={classCustom}>
                      WebSite Link
                    </span>
                    <span className={"text-md text-primary"}>
                     <BlockLink content={data?.webSiteLink} />
                    </span>
                  </div>}
                  {data?.notes &&
                    <div className="flex flex-col justify-between space-y-2 mt-8">
                      <span className={classCustom}>
                        Notes
                      </span>
                      <span className={"text-md text-primary overflow-y-auto h-16"}>
                        {data?.notes}
                      </span>
                    </div>
                  }
                  <div className="w-full flex justify-end items-end rounded-xl">
                    <button onClick={() => handleUpdateComposer(data?.id)} className="whitespace-nowrap px-3 py-1.5 bg-pblue text-white text-xs rounded font-medium border-white text-white">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="inline w-4 mb-1 h-4 mr-1">
                        <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z" />
                        <path d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z" />
                      </svg>
                      Update
                    </button>
                  </div>
                </div>
              </div>
              <CloseModal onClick={() => setOpen(false)} />
             
            </div>
          </Transition.Child >
        </div >
      </Dialog >
    </Transition.Root>
  </>
  );
}
